import React, { PureComponent } from 'react'

import ProductService from '../services/ProductService'
import CommentService from '../services/CommentService'
import PromotionBar from '../components/Store/Product/PromotionBar'
import LoadingModal from '../components/common/LoadingPage/LoadingModal'
import ProductModal from '../components/Store/Cart/ProductModal'
import ProductDrawer from '../components/Store/Cart/ProductDrawer'
import FlashPromotionBar from '../components/Store/Product/FlashPromotionBar'
import ProductPromotionBar from '../components/Store/Product/ProductPromotionBar'
import { getVariantsByVariantOptions, getExactMatchingVariant } from '../utils/Store/productUtils'
import withCartAndPlugins from './withCartAndPlugins'
import { hoistHocStatic } from '@flamingo_tech/funkgo'
import { setDefaultSelectedOptionValues, isVariantsDisabledByVariantOptions, OPTION_COLOR_SEPARATOR } from '../utils/Store/productUtils'
import { DynamicProductVariant } from '../npm/FunkGoModel/ProductModel'
import { rem as px2rem } from '../utils/remUtils'

/* ---------------------------------- */
const withQuickAdd = (Component, { eventCategory } = {}, staticProps) => {

  const displayName = `withQuickAdd(${Component.displayName || Component.name})`

  class WrappedComponent extends PureComponent {
    state = {
      product: {},
      showSpecSelectionModal: false,
      productDetailFetched: false,
      comment: {},
      curImageIndex: 0,
      selectedVariantInfo: {},
      forceUpdateSelectedVariantsCounter: 0,
      curImages: [],
      showLoadingModal: false,
      isListing: false
    }

    productService = new ProductService(this.props.$http)
    commentService = new CommentService(this.props.$http)
    _atcTriggerRef = null
    _listingData = null
    _listingProductMap = {}

    // need return promise
    handleQuickAdd = ({ product, quickAddInfo, selectedVariantId, selectedVariantInfo, refInfo }) => {
      const { triggerRef = null } = refInfo || {}

      this._atcTriggerRef = triggerRef

      if (quickAddInfo) {
        const { product, comment, selectedVariantInfo, renderLink, isListing, listingProductMap } = quickAddInfo
        const productWithoutVideo = {
          ...(product || {}),
          images: ((product || {}).images || []).filter(item => item.type !== 'VIDEO')
        }

        this.setState({
          product: productWithoutVideo,
          productDetailFetched: true,
          showSpecSelectionModal: true,
          comment: comment || {},
          selectedVariantInfo: selectedVariantInfo || {},
          renderLink,
          isListing
        })
        this._listingProductMap = listingProductMap || {}
        this.props.onQuickAddModalOpened && this.props.onQuickAddModalOpened()

        setTimeout(() => {
          this.getCurImagesByOption(selectedVariantInfo, true, productWithoutVideo)
        })
        return this.createDeferredPromise()
      }

      let { handle } = product

      if (!handle) {
        return
      }

      this.setState({
        showLoadingModal: true
      })

      const fetchDynamicProductReq = this.productService.fetchDynamicProduct(handle, { priceTag: this.props.priceTag }).catch(() => null)
      const fetchProductSizeInfoReq = this.productService.fetchProductSizeInfo(handle).catch(() => {})

      return Promise.all([this.productService.fetchProduct(handle, { priceTag: this.props.priceTag }), fetchDynamicProductReq, fetchProductSizeInfoReq])
        .then(([product, dynamicProduct, productSizeInfo]) => {

          const { mainVariation: oldMainVariation, variations  } = product

          let updatedProduct = {
            ...product,
            ...productSizeInfo,
            images: (product.images || []).filter(item => item.type !== 'VIDEO')
          }

          let updatedVariations = [...variations]

          if (dynamicProduct) { // update data if has
            const { availableForSale, mainVariation, variationsMap } = dynamicProduct

            updatedVariations = variations.map(variation => ({
              ...variation,
              ...(variationsMap[variation.id] ? new DynamicProductVariant(variationsMap[variation.id]) : { availableForSale: false })
            }))

            updatedProduct = {
              ...updatedProduct,
              availableForSale,
              variations: updatedVariations
            }

            if (availableForSale && updatedVariations.filter(variation => variation.availableForSale).length === 0) {
              updatedProduct.availableForSale = false
            }


            if (!updatedProduct.availableForSale) {
              this.props.$track.event('Product', 'outOfStock', updatedProduct.handle)
            }

            if (oldMainVariation.id === mainVariation.id) {
              updatedProduct.mainVariation = Object.assign({}, oldMainVariation, new DynamicProductVariant(mainVariation))
            }
          }

          let newSelectedVariantInfo = selectedVariantInfo

          if (selectedVariantId) {
            const matchedVariation = updatedVariations.filter(variation => variation.id === selectedVariantId)[0]

            if (matchedVariation) {
              const result = {}

              matchedVariation.selectedOptions.forEach(item => {
                result[item.name] = item.value
              })

              newSelectedVariantInfo = result
            }
          }

          const disabled = isVariantsDisabledByVariantOptions({ ...newSelectedVariantInfo }, updatedVariations)
          const selectedVariants = newSelectedVariantInfo || setDefaultSelectedOptionValues(updatedProduct)

          this.setState({
            product: updatedProduct,
            productDetailFetched: true,
            showSpecSelectionModal: true,
            showLoadingModal: false,
            selectedVariantInfo: disabled ? setDefaultSelectedOptionValues(updatedProduct) : selectedVariants,
          })

          this.props.onQuickAddModalOpened && this.props.onQuickAddModalOpened()
          this.getCurImagesByOption({}, null, updatedProduct)

          this.fetchProductListing(handle, updatedProduct, { priceTag: this.props.priceTag })
          this.fetchListingProductMap(handle)

          this.commentService.fetchProductCommentInfo(handle).then(comment => {
            this.setState({
              comment: {
                ...comment,
                overallCount: comment.count
              }
            })
          })
        })
        .catch(() => {
          this.setState({
            showLoadingModal: false
          })
        })
    }

    createDeferredPromise() {
      return new Promise((resolve, reject) => {
        this.deferred = { resolve, reject }
      })
    }

    resolveDeferred(payload) {
      if (this.deferred) {
        this.deferred.resolve(payload)
      }
      this.deferred = null
    }

    rejectDeferred(err) {
      if (this.deferred) {
        this.deferred.reject(err)
      }
      this.deferred = null
    }

    closeSpecSelectionModal = () => {
      this.resetState()
    }

    isEqual = (options1, options2) => {
      const options1Value1 = options1.map(i => i.value).join('/')
      const options1Value2 = options2.map(i => i.value).join('/')
      return options1Value1 === options1Value2
    }

    handleSpecSelectionConfirm = ({ selectedVariant, selectedQuantity }) => {
      if (this.props.isUpdateMode) {
        return new Promise((resolve, reject) => {

          const isEqualVariant = this.isEqual(selectedVariant.selectedOptions, this.props.selectedOptions)

          if (isEqualVariant) {
            this.resetState()
            resolve()
            return
          }

          const replaceFn = this.props.replaceVariant || this.props.$replaceVariant

          replaceFn({ variant: selectedVariant, itemId: this.props.product.itemId })
            .then(() => {
              this.resetState(true)
              resolve()
            })
            .catch(error => {
              this.props.$toastError(error.message)
              this.resetState()
              reject(error)
            })

          this.resolveDeferred({ variant: selectedVariant, quantity: selectedQuantity })

        })
      } else {
        const addFn = this.props.addVariant || this.props.$addVariant // self atc function || default atc function

        return new Promise((resolve, reject) => {
          addFn({
            variant: selectedVariant,
            quantity: selectedQuantity,
            product: this.state.product || {},
            needFetchFullAfterAddCart: this.props.needFetchFullAfterAddCart,
            priceTag: this.props.priceTag
          }).then(() => {

            this.props.updatePrice && this.props.updatePrice()
            this.resetState(true)

            if (!this.props.hideAtcSuccessEffect) {
              setTimeout(() => {
                if (!this.props.$store.startAtcParabola({
                  startRef: this._atcTriggerRef
                })) {
                  this.props.$toastSuccess({
                    content: this.props.$i18n.transl('core.cart.addSuccess'),
                    duration: 1500
                  })
                }
              })
            }

            resolve()
          }).catch(error => {
            this.props.$toastError(error.message)
            this.resetState()
            reject(error)
          })

          this.resolveDeferred({ variant: selectedVariant, quantity: selectedQuantity })
        })
      }
    }

    resetState = (isAtcSucceed) => {
      this.setState({
        product: {},
        productDetailFetched: false,
        showSpecSelectionModal: false,
        selectedVariantInfo: {}
      })
      this.props.onQuickAddModalClosed && this.props.onQuickAddModalClosed(isAtcSucceed)
    }

    getSortedProductImages = (images, options) => {
      const tagImages = []

      options.forEach((option) => {
        if (option.name === 'Color') {
          option.values.forEach(opt => {
            images.forEach(image => {
              const { tag } = image
              if (tag && tag.optionValue && tag.optionValue === opt) {
                tagImages.push(image)
              }
            })
          })
        }
      })

      // filter untagImages
      const untagImages = images.filter(image => tagImages.indexOf(image) <= -1)

      return [...tagImages, ...untagImages]
    }

    getCurImagesByOption = (currentVariants = {}, force, product) => {
      let imgs = []
      const { images = [], options = [] } = product || this.state.product || {}
      const hasSelected = Object.keys(currentVariants).length > 0

      const selectedOption = hasSelected ? currentVariants.Color : this.state.selectedVariantInfo.Color

      if (currentVariants.Color  === this.state.selectedVariantInfo.Color  && !force) {
        return
      }

      if (this.state.isListing) {
        let formattedSelectedOption = currentVariants.Color

        if (formattedSelectedOption && formattedSelectedOption.indexOf(OPTION_COLOR_SEPARATOR) > -1) {
          formattedSelectedOption = formattedSelectedOption.split(OPTION_COLOR_SEPARATOR)[0]
        }
        imgs = images.filter(image => {
          const { tag } = image
          if (tag && tag.optionValue && tag.optionValue === formattedSelectedOption) {
            return true
          } else {
            return false
          }
        })
        this.setState({
          curImageIndex: 0
        })
      } else {
        imgs = this.getSortedProductImages(images, options)

        for (let i = 0; i < imgs.length; i++) {
          const { tag } = imgs[i]
          if (tag && tag.optionValue && tag.optionValue === selectedOption) {
            this.setState({
              curImageIndex: i
            })
            break
          }
        }
      }

      this.setState({
        curImages: imgs
      })
    }

    onOptionSelectedChanged = (data) => {
      const previousSelectedVariantInfo = this.state.selectedVariantInfo

      this.setState({
        selectedVariantInfo: data
      })

      if (this.state.isListing) {
        this.onScuChange(data.Color).then(() => {
          this.getCurImagesByOption(data, true)
        }).catch(() => {
          this.setState({
            selectedVariantInfo: previousSelectedVariantInfo,
            forceUpdateSelectedVariantsCounter: this.state.forceUpdateSelectedVariantsCounter + 1
          })
        })
      } else {
        this.getCurImagesByOption(data) // 更换图集
      }
    }

    onScuChange = (scuValue) => {
      const listingHandle = scuValue.split(OPTION_COLOR_SEPARATOR)[1]

      if (listingHandle !== this.state.product.handle) {

        const switchedProduct = this._listingProductMap[listingHandle]

        if (switchedProduct) {
          this.setState({
            product: {
              ...switchedProduct,
              images: (switchedProduct.images || []).filter(item => item.type !== 'VIDEO'),
              options: this.state.product.options,
              colorSwatchList: this.state.product.colorSwatchList,
              variations: this.state.product.variations,
              mainVariation: this.state.product.variations.filter(item => item.handle === listingHandle && item.availableForSale)[0]
            },
            comment: {
              ...switchedProduct.comment,
              overallCount: switchedProduct.comment.count
            }
          })

          const fetchProductReq = this.productService.fetchProduct(listingHandle, { priceTag: this.props.priceTag }).catch(() => ({}))
          const fetchProductSizeInfoReq = this.productService.fetchProductSizeInfo(listingHandle).catch(() => ({}))

          Promise.all([fetchProductReq, fetchProductSizeInfoReq])
            .then(([product, productSizeInfo]) => {
            this.setState({
              product: {
                ...product,
                ...productSizeInfo,
                ...this.state.product // 总是使用listing商品数据覆盖静态数据
              },
              productDetailFetched: true,
              showSpecSelectionModal: true
            })

            this.props.onQuickAddModalOpened && this.props.onQuickAddModalOpened()
          })

          return Promise.resolve()
        } else {
          this.setState({
            showLoadingModal: true
          })

          const fetchProductSizeInfoReq = this.productService.fetchProductSizeInfo(listingHandle).catch(() => {})

          return Promise.all([this.productService.fetchProduct(listingHandle, { priceTag: this.props.priceTag }), fetchProductSizeInfoReq])
            .then(([product, productSizeInfo]) => {

              let updatedProduct = {
                ...product,
                ...productSizeInfo,
                images: (product.images || []).filter(item => item.type !== 'VIDEO')
              }

              this.setState({
                product: updatedProduct,
                productDetailFetched: true,
                showSpecSelectionModal: true,
                showLoadingModal: false
              })

              this.props.onQuickAddModalOpened && this.props.onQuickAddModalOpened()
              this.getCurImagesByOption(this.state.selectedVariantInfo, null)

              this.commentService.fetchProductCommentInfo(listingHandle).then(comment => {
                this.setState({
                  comment: {
                    ...comment,
                    overallCount: comment.count
                  }
                })
              })
              this.fetchProductListing(listingHandle, updatedProduct, {
                needReselectCurrentVariant: false,
                priceTag: this.props.priceTag
              })
            })
            .catch(() => {
              this.setState({
                showLoadingModal: false
              })
              return Promise.reject()
            })
        }
      } else {
        return Promise.resolve()
      }
    }

    fetchListingProductMap = handle => {
      this.productService.fetchListingProductMap(handle).then(data => {
        this._listingProductMap = data.productMap || {}
      })
    }

    fetchProductListing = (handle, product, { needReselectCurrentVariant = true, priceTag } = {}) => {

      const promiseFn = (!needReselectCurrentVariant && this._listingData) ? Promise.resolve(this._listingData) : this.productService.fetchProductListing(handle, { priceTag })

      promiseFn.then(data => {
        this._listingData = data

        const { isListing, options, colorSwatchList, listingVariations } = data

        if (!isListing) {
          return
        }

        const { mainVariation: oldMainVariation, variations } = product

        let mainVariation = oldMainVariation

        // 当前商品商详数据，一一去 listing 全集 sku 里匹配
        const variationIds = new Set(variations.map(v => v.id))

        const updatedVariations = variations.map(variation => {
          const matchedVariation = listingVariations.find(item => item.id === variation.id)

          const combinedVariation = matchedVariation
            ? { ...variation, ...matchedVariation }
            : { ...variation, availableForSale: false }

          if (combinedVariation.id === mainVariation.id) {
            mainVariation = Object.assign({}, oldMainVariation, combinedVariation)
          }

          return combinedVariation
        })

        // listing 全集里去除当前商品已有的 sku，剩下的直接平移
        const extraVariations = listingVariations.filter(item => !variationIds.has(item.id))

        const finalVariations = [...updatedVariations, ...extraVariations]

        const updatedProduct = {
          ...product,
          options,
          colorSwatchList,
          mainVariation,
          variations: finalVariations,
        }

        const updateProps = {
          product: updatedProduct,
          isListing
        }

        // listing商品的color一定有分隔符，但购物车的case会需要自己拼。这里相当于做个修正
        const curSelectedVariantInfo = this.state.selectedVariantInfo.Color && this.state.selectedVariantInfo.Color.indexOf(OPTION_COLOR_SEPARATOR) <= -1 ?
        {
          ...this.state.selectedVariantInfo,
          Color: `${this.state.selectedVariantInfo.Color}${OPTION_COLOR_SEPARATOR}${updatedProduct.handle}`
        } : this.state.selectedVariantInfo

        if (needReselectCurrentVariant) {
          const disabled = isVariantsDisabledByVariantOptions({ ...curSelectedVariantInfo }, updatedVariations)
          const selectedVariants = curSelectedVariantInfo || setDefaultSelectedOptionValues(updatedProduct)
          updateProps.selectedVariantInfo = disabled ? setDefaultSelectedOptionValues(updatedProduct) : selectedVariants
          updateProps.forceUpdateSelectedVariantsCounter = this.state.forceUpdateSelectedVariantsCounter + 1
        }

        this.setState(updateProps)

        this.getCurImagesByOption(needReselectCurrentVariant ? updateProps.selectedVariantInfo : curSelectedVariantInfo, true, updatedProduct)
      }).catch(() => {
        this._listingData = null
        this.setState({
          isListing: false
        })
      })
    }


    renderPromotionBar = product => {
      const { meta } = product

      if (meta && meta.feature && meta.feature.promotionBar) {
        return (
          <ProductPromotionBar {...meta.feature.promotionBar} />
        )
      }
      const { msrp, price } = product.mainVariation || {}
      const hasFlashDiscount = !!(msrp && msrp > price && product.isFlashSale)

      if (hasFlashDiscount) {
        const isDesktop = this.props.$detector.isDesktop()
        const style = { width: px2rem(isDesktop ? 409 : 375) }
        return <div style={style}><FlashPromotionBar product={product} /></div>
      }

      const bestCoupon = this.props.$store
        ? this.props.$store.couponHub.pickBestForProduct(product)
        : undefined

      if (!bestCoupon) {
        return null
      }

      const { productDetail } = bestCoupon.meta.layout || {}
      if (productDetail && productDetail.is === 'PromotionBar') {
        return (
          <PromotionBar
            $i18n={this.props.$i18n}
            promotion={bestCoupon}
            {...productDetail.options}
          />
        )
      } else {
        return null
      }
    }

    generateCartInfo = () => {
      const { product } = this.state
      const cart = {
        selectedVariant: null,
        selectedOptions: this.state.selectedVariantInfo,
        selectedQuantity: 1
      }

      const exactVariant = getExactMatchingVariant(this.state.selectedVariantInfo, product.variations)
      if (exactVariant) {
        cart.selectedVariant = exactVariant
        return cart
      } else {
        return cart
      }
    }

    generateVariantWithoutChosen = (selectedVariants, variants) => {
      return variants.filter(variant => {
        return !selectedVariants[variant.name]
      })[0]
    }

    handleAddToCartClick = () => {
      // directly add to cart without open modal when there is default variant
      const { product } = this.state
      if (product.availableForSale) {
        if (this.state.selectedVariantInfo) {
          const selectedVariants = this.state.selectedVariantInfo
          const cart = this.generateCartInfo()

          if (cart.selectedVariant) {
            return this.handleSpecSelectionConfirm(cart)
          } else {
            this.props.$toastWarning({
              content: this.props.$i18n.transl('core.product.chooseOption', {
                option: this.generateVariantWithoutChosen(selectedVariants, this.state.product.options).displayName
              }),
              dialogId: 'product-add'
            })
            return Promise.resolve()
          }
        }
      } else {
        // out of stock
        this.props.$router.navigateToMessengerBot('onshelfSubscription', product.handle, () => { })
        return Promise.resolve()
      }
    }

    hasOneSelectedVariant = (options) => {

      return Object.keys(this.state.selectedVariantInfo).length === options.length
    }

    fetData = (product = {}, showSpecSelectionModal) => {

      if (showSpecSelectionModal && product.handle) {
        const { feature = {} } = product.meta || {}
        const { tag } = feature

        const curSelectedVariants = getVariantsByVariantOptions(this.state.selectedVariantInfo, product.variations)
        const variantInfo = this.hasOneSelectedVariant(product.options) ? curSelectedVariants[0] : product.mainVariation
        return {
          tag: tag,
          product: product,
          comment: this.state.comment,
          variantInfo: variantInfo,
          curImageIndex: this.state.curImageIndex,
          curImages: this.state.curImages.length > 0 ? this.state.curImages : product.images,
          selectedVariants: this.state.selectedVariantInfo,
          forceUpdateSelectedVariantsCounter: this.state.forceUpdateSelectedVariantsCounter,
          onOptionSelectedChanged: this.onOptionSelectedChanged,
          renderPromotionBar: this.renderPromotionBar,
          minSliderWrapper: true,
          renderLink: this.state.renderLink
        }
      }
      return false
    }

    render() {
      const isDesktop = this.props.$detector.isDesktop()
      const renderProps = this.fetData(this.state.product, this.state.showSpecSelectionModal)
      const ProductDrawerComponent = this.props.CustomizedProductDrawer || (isDesktop ? ProductModal : ProductDrawer)

      return (
        <>
          <LoadingModal hide={!this.state.showLoadingModal}></LoadingModal>
          <Component
            {...this.props}
            onQuickAdd={this.handleQuickAdd}
            eventCategory={eventCategory}
            resetState={this.resetState}
          ></Component>
          {
            this.state.showSpecSelectionModal && renderProps &&
            <ProductDrawerComponent
              {...renderProps.product}
              {...renderProps}
              headerTitle={isDesktop && this.props.$i18n.transl('core.cart.editSelection')}
              onAddToCartClick={this.handleAddToCartClick}
              onClose={this.closeSpecSelectionModal}
              onDrawerClose={() => this.setState({ showSpecSelectionModal: false })}
              isUpdateMode={this.props.isUpdateMode}
            />
          }
        </>
      )
    }
  }

  WrappedComponent.displayName = displayName
  hoistHocStatic(WrappedComponent, Component, staticProps)

  return withCartAndPlugins(WrappedComponent)
}

export default withQuickAdd
